import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Info, Phone } from 'lucide-react';

import img from "../Images/manifold.webp";
import img1 from "../Images/dea.jpg";
import defaultImg from "../Images/vaccum.jpeg";
import img2 from "../Images/manispec.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');

  return (
    <article className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6">
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">{product.name}</h3>
      </header>
      
      <div className="bg-gray-100 h-40 sm:h-48 md:h-56 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - industrial steam accessories for boiler systems`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          loading="lazy"
          width="400"
          height="300"
        />
      </div>

      <div className="mb-4">
        <nav className="flex border-b border-gray-200" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'description'}
            aria-controls={`${product.id}-description`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'description'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('description')}
          >
            Description
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'specs'}
            aria-controls={`${product.id}-specs`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'specs'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('specs')}
          >
            Specifications
          </button>
        </nav>

        <div className="mt-4">
          {activeTab === 'description' ? (
            <div id={`${product.id}-description`} role="tabpanel">
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-justify">
                {product.description}
              </p>
            </div>
          ) : (
            <div id={`${product.id}-specs`} role="tabpanel">
              <div className="bg-gray-100 h-32 sm:h-40 rounded-lg overflow-hidden mb-4">
                <img 
                  src={product.specImage}
                  alt={`${product.name} technical specifications chart`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width="400"
                  height="200"
                />
              </div>
              <ul className="space-y-2">
                {product.specifications.map((spec, index) => (
                  <li key={index} className="flex items-start">
                    <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-xs sm:text-sm text-gray-600">{spec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const Accessories = () => {
  const products = [
    {
      id: 'manifold-valves',
      name: 'Manifold Valves',
      mainImage: img,
      specImage: img2,
      description: `Our Manifold Valves are engineered to provide reliable instrument isolation and precise flow control in demanding industrial applications. These valves feature a compact design that reduces potential leak paths while offering multiple valve configurations in a single unit. The robust construction ensures long-term reliability and safe operation in critical processes. Perfect for instrumentation systems, these manifolds provide excellent pressure and temperature control while minimizing installation space and maintenance requirements.`,
      specifications: [
        'Pressure ratings up to 6000 PSI (414 bar)',
        'Temperature range: -50°C to +450°C',
        'Available in 2-valve, 3-valve, and 5-valve configurations',
        'Materials: 316/316L SS, Carbon Steel, Alloy 20',
        'Multiple packing material options',
        'Integral bonnet locks for enhanced safety',
        'Available with various end connection options',
        'Anti-tampering design features'
      ],
      keywords: 'manifold valves, instrument valves, industrial valves, pressure control'
    },
    {
      id: 'vacuum-breaker',
      name: 'Vacuum Breaker',
      mainImage: defaultImg,
      specImage: img2,
      description: `The Vacuum Breaker is designed to protect systems from vacuum conditions that can cause equipment damage and process disruption. This essential safety device automatically admits air into the system when vacuum conditions occur, preventing collapse or damage to tanks, pipelines, and process equipment. The innovative design ensures quick response to vacuum conditions while maintaining a reliable seal during normal operation. Our vacuum breakers feature maintenance-friendly designs and robust construction for long-term reliability.`,
      specifications: [
        'Opening pressure: 0.02 to 0.05 bar vacuum',
        'Maximum operating temperature: 200°C',
        'Body materials: Bronze, Stainless Steel, Cast Iron',
        'Sizes available: DN15 to DN100',
        'Self-cleaning seat design',
        'Maintenance-free operation',
        'Optional position indicator',
        'Various mounting configurations available'
      ],
      keywords: 'vacuum breaker, safety valve, vacuum protection, industrial safety equipment'
    },
    {
      id: 'deaerator-head',
      name: 'Deaerator Head',
      mainImage: img1,
      specImage: img2,
      description: `Our Deaerator Head represents the latest in dissolved gas removal technology for boiler feed water systems. This crucial component efficiently removes dissolved oxygen and other harmful gases from feed water, preventing corrosion and improving system efficiency. The design incorporates advanced spray nozzle technology and optimized steam distribution to achieve maximum oxygen removal efficiency. The robust construction and precise engineering ensure reliable performance in demanding industrial applications while maintaining optimal heat transfer efficiency.`,
      specifications: [
        'Oxygen removal to less than 0.005 cc/l',
        'Capacity range: 1,000 to 500,000 kg/hr',
        'Operating pressure: 0.2 to 3.0 bar g',
        'Maximum temperature: 250°C',
        'Stainless steel spray nozzles',
        'Internal vent condenser',
        'Level control system interface',
        'ASME Section VIII construction'
      ],
      keywords: 'deaerator head, boiler feed water treatment, oxygen removal, water deaeration'
    }
  ];

  // Structured data for product page
  const productListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial Steam Accessories",
    "description": "High-quality industrial accessories including manifold valves, vacuum breakers, and deaerator heads for boiler systems",
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.mainImage,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "price": "Contact for pricing",
          "priceCurrency": "USD"
        }
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://www.gorthermenergy.com/products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Accessories",
        "item": "https://www.gorthermenergy.com/products/accessories"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Optimized title (under 60 characters) */}
        <title>Industrial Steam Accessories | Valves & Equipment Nepal</title>
        
        {/* Optimized meta description (120-160 characters) */}
        <meta name="description" content="Premium industrial steam accessories including manifold valves, vacuum breakers, and deaerator heads. Expert installation services in Nepal." />
        
        {/* Keywords */}
        <meta name="keywords" content="industrial accessories, manifold valves, vacuum breaker, deaerator head, steam system components, boiler accessories, industrial valves Nepal" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/products/accessories" />
        
        {/* Language */}
        <html lang="en" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Industrial Steam Accessories | Gortherm Energy" />
        <meta property="og:description" content="High-quality industrial accessories for boiler and steam systems. Manifold valves, vacuum breakers, and deaerator heads." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/products/accessories" />
        <meta property="og:image" content={img} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Steam Accessories" />
        <meta name="twitter:description" content="Premium industrial accessories for boiler systems in Nepal" />
        <meta name="twitter:image" content={img} />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productListSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      </Helmet>

      <div className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          


          {/* Hero Section */}
          <header className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Industrial Steam Accessories
            </h1>
            <h2 className="text-xl sm:text-2xl text-gray-700 mb-4">
              Premium Quality Components for Boiler Systems
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Discover our comprehensive range of high-quality industrial steam accessories, engineered to enhance system performance, ensure reliable operation, and maximize efficiency across all applications. From manifold valves to deaerator heads, we provide professional-grade components for industrial boiler systems in Nepal.
            </p>
          </header>

          {/* Product Grid */}
          <section aria-label="Industrial accessories catalog">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>

          {/* Additional SEO Content */}
          <section className="mt-12 sm:mt-16 bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Why Choose Our Industrial Accessories?
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Premium Quality</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  All accessories manufactured to international standards with rigorous quality control ensuring long-term reliability and performance.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Expert Installation</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Professional installation services by certified technicians ensuring proper setup and optimal system integration.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Wide Selection</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Comprehensive range of accessories suitable for various industrial applications and system requirements.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Technical Support</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Ongoing technical assistance and maintenance support ensuring continuous operation and system efficiency.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Competitive Pricing</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Cost-effective solutions with transparent pricing and excellent value for industrial-grade components.
                </p>
              </article>
              
              <article className="bg-gray-50 p-6 rounded-xl">
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Local Availability</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Quick delivery and readily available spare parts across Nepal ensuring minimal downtime.
                </p>
              </article>
            </div>
          </section>

          {/* Applications Section */}
          <section className="mt-12 sm:mt-16 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 md:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Industrial Applications
            </h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Boiler Systems</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  Essential components for industrial and commercial boiler installations including feed water treatment, pressure control, and safety systems.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Steam Distribution</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  Critical accessories for steam pipeline systems ensuring efficient distribution, condensate management, and system protection.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Process Industries</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  Specialized components for manufacturing facilities including textile, food processing, pharmaceutical, and chemical industries.
                </p>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3 text-gray-900">Power Generation</h3>
                <p className="text-sm sm:text-base text-gray-700 mb-4">
                  High-performance accessories for power plants and cogeneration facilities ensuring reliable energy production.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="mt-12 sm:mt-16 text-center bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Need Expert Guidance?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our specialists are here to help you select the perfect industrial accessories for your specific application. Get professional consultation and technical support for your project.
            </p>
            <a href="tel:+9779802781400" className="inline-block">
              <button className="bg-teal-600 hover:bg-teal-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
                <Phone className="w-5 h-5" aria-hidden="true" />
                <span className="text-sm sm:text-base font-semibold">Contact Our Experts</span>
              </button>
            </a>
            
          </section>
        </div>
      </div>
    </>
  );
};

export default Accessories;